import { useState } from 'react';
import config from '../config/config';
import { useAuth } from '../contexts/AuthContext';
import authHeader from '../config/authHeader';

const useFetchIntervenants = () => {
    const { token, logout } = useAuth();

    const [popupMode, setPopupMode] = useState("add");

    const [intervenants, setIntervenants] = useState([]);
    const [intervenant, setIntervenant] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [relIntervenants, setRelIntervenants] = useState([]);
    const [cas, setCas] = useState([]);
    const [detailsProvisions, setDetailsProvisions] = useState([]);
    const [declarationIntervenants, setDeclarationIntervenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getHeaders = () => ({
        Authorization: `Bearer ${token}`,
    });

    const getHeadersWithJson = () => ({
        ...authHeader(),
        'Content-Type': 'application/json',
    });

    const getHeadersWithFormData = () => authHeader();

    const fetchHelper = async (url, options = {}, setState = null) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(url, {
                ...options,
                headers: authHeader(),
            });

            if (response.status === 401) return logout();
            if (!response.ok) throw new Error(`Erreur lors de la requête : ${url}`);

            const data = await response.json();
            if (setState) setState(data);
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const postForm = async (url, payload) => {
        setLoading(true);
        setError(null);
        const formData = new FormData();
        for (const key in payload) {
            if (payload.hasOwnProperty(key)) {
                formData.append(key, payload[key]);
            }
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: getHeadersWithFormData(),
                body: formData,
            });

            if (response.status === 401) return logout();
            if (!response.ok) throw new Error(`Erreur lors de l'envoi POST à ${url}`);

            return await response.json();
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // === Méthodes individuelles ===
    const fetchIntervenants = () => fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getIntervenants`, {}, setIntervenants);
    const fetchIntervenantByUUID = async (uuid) => {
        const data = await fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getIntervenantByUUID?uuid=${uuid}`);
        if (data) setIntervenant(data[0]);
    };

    const fetchAddIntervenant = (intervenant) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_createIntervenant`, intervenant);

    const fetchContactsByIntervenantUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getContactsByInterUUID?uuid=${uuid}`, {}, setContacts);

    const fetchAddContact = (contact) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_createContact`, contact);

    const fetchDetailProvisionsByDommageUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getDetProsByDomUUID?uuid=${uuid}`, {}, setDetailsProvisions);

    const fetchUpdateDetailProvision = (detPro) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_editDetPro`, detPro);

    const fetchDeleteDetailProvisionByUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_deleteDetProByUUID?uuid=${uuid}`);

    const fetchAddDetailProvision = (detPro) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_createDetPro`, detPro);

    const fetchDeclaIntByDeclaUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getDeclaIntByDeclaUUID?uuid=${uuid}`, {}, setDeclarationIntervenants);

    const fetchUpdateDeclaInt = (declaInt) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_editDeclaInt`, declaInt);

    const fetchChargesAffairesByLicence = () =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getChargesAffairesByLicence`, {}, setCas);

    const fetchRelationsIntervenants = () =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getRelationsIntervenants`, {}, setRelIntervenants);

    const fetchRelationsIntervenantsBySocieteUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getRelInterByInterUUID?uuid=${uuid}`, {}, setRelIntervenants);

    const fetchRelationsIntervenantsBySiteUUID = (uuid) =>
        fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getRelInterBySiteUUID?uuid=${uuid}`, {}, setRelIntervenants);

    const fetchAddIntervenantToDeclaration = (uuidDecla, uuidIntervenant) =>
        postForm(`${config.apiBaseUrl}/4DACTION/KST_addIntervenantToDecla`, { uuidDecla, uuidIntervenant });

    const fetchContactsIntervenantsByUUIDs = async (UUIDs) => {
        try {
            const UUIDsLst = JSON.stringify(UUIDs || []);

            const data = await postForm(
                `${config.apiBaseUrl}/4DACTION/KST_getContactsIntByUUIDs`,
                { UUIDsLst }
            );

            if (data) {
                setContacts(Array.isArray(data) ? data : []);
            } else {
                setContacts([]);
            }
            return data;
        } catch (e) {
            setContacts([]);
            return null;
        }
    };

    const fetchContactsIntervenantsBySiteUUID = (siteUUID) => {
        return fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getContactsIntBySiteUUID?siteUUID=${siteUUID}`, {}, setContacts);
    };

    const fetchContactsIntervenants = () => {
        return fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getContactsInts`, {}, setContacts);
    };

    const fetchInvitationsDeclarations = () => {
        return fetchHelper(`${config.apiBaseUrl}/4DACTION/KST_getInvitationsDeclarations`, {}, setContacts);
    };

    return {
        intervenants,
        intervenant,
        contacts,
        relIntervenants,
        cas,
        detailsProvisions,
        declarationIntervenants,
        loading,
        error,
        fetchIntervenants,
        fetchIntervenantByUUID,
        fetchAddIntervenant,
        fetchContactsByIntervenantUUID,
        fetchAddContact,
        fetchRelationsIntervenants,
        fetchRelationsIntervenantsBySocieteUUID,
        fetchRelationsIntervenantsBySiteUUID,
        fetchChargesAffairesByLicence,
        fetchDetailProvisionsByDommageUUID,
        fetchUpdateDetailProvision,
        fetchDeleteDetailProvisionByUUID,
        fetchDeclaIntByDeclaUUID,
        fetchAddDetailProvision,
        fetchUpdateDeclaInt,
        fetchAddIntervenantToDeclaration,
        fetchContactsIntervenantsByUUIDs,
        fetchContactsIntervenantsBySiteUUID,
        fetchContactsIntervenants,
    };
};

export default useFetchIntervenants;
